import { Box, Link, Typography, useTheme } from "@mui/material";
import React from "react";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import RemarkBreaks from "remark-breaks";
import RemarkGfm from "remark-gfm";

export interface MarkdownProps {
  content: string;
}

function escapeBrackets(text: string) {
  const pattern = /(```[\s\S]*?```|`.*?`)|\\\[([\s\S]*?[^\\])\\\]|\\\((.*?)\\\)/g;
  return text.replace(pattern, (match, codeBlock, squareBracket, roundBracket) => {
    if (codeBlock) {
      return codeBlock;
    } else if (squareBracket) {
      return `$$${squareBracket}$$`;
    } else if (roundBracket) {
      return `$${roundBracket}$`;
    }
    return match;
  });
}

function tryWrapHtmlCode(text: string) {
  if (text.includes("```")) {
    return text;
  }
  return text
    .replace(/([`]*?)(\w*?)([\n\r]*?)(<!DOCTYPE html>)/g, (match, quoteStart, doctype) => {
      return !quoteStart ? "\n```html\n" + doctype : match;
    })
    .replace(
      /(<\/body>)([\r\n\s]*?)(<\/html>)([\n\r]*)([`]*)([\n\r]*?)/g,
      (match, bodyEnd, space, htmlEnd, quoteEnd) => {
        return !quoteEnd ? bodyEnd + space + htmlEnd + "\n```\n" : match;
      }
    );
}

export default function Markdown({ content }: MarkdownProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const escapedContent = useMemo(() => {
    return tryWrapHtmlCode(escapeBrackets(content));
  }, [content]);

  return (
    <Box sx={{ wordWrap: "break-word" }}>
      <ReactMarkdown
        remarkPlugins={[RemarkGfm, RemarkBreaks]}
        components={{
          a: ({ children, href }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </Link>
          ),
          p: ({ children }) => (
            <Typography variant="body1" sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {children}
            </Typography>
          ),
          strong: ({ children }) => (
            <Typography component="span" sx={{ fontWeight: "bold" }}>
              {children}
            </Typography>
          ),
          em: ({ children }) => (
            <Typography component="span" sx={{ fontStyle: "italic" }}>
              {children}
            </Typography>
          ),
          code: ({ children }) => (
            <Typography
              component="code"
              sx={{
                backgroundColor: isDarkMode ? theme.palette.grey[100] : theme.palette.grey[200],
                color: isDarkMode ? theme.palette.text.primary : theme.palette.grey[800],
                padding: "2px 4px",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontSize: "0.875rem",
              }}
            >
              {children}
            </Typography>
          ),
          pre: ({ children }) => (
            <Box
              component="pre"
              sx={{
                backgroundColor: isDarkMode ? theme.palette.grey[700] : theme.palette.grey[100],
                color: isDarkMode ? theme.palette.action[100] : theme.palette.grey[800],
                padding: 1,
                borderRadius: 1,
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "0.875rem",
                margin: "8px 0",
              }}
            >
              {children}
            </Box>
          ),
          ul: ({ children }) => (
            <Box component="ul" sx={{ pl: 2, mb: 1 }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 2, mb: 1 }}>
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
              {children}
            </Typography>
          ),
          blockquote: ({ children }) => (
            <Box
              sx={{
                borderLeft: `4px solid ${theme.palette.grey[400]}`,
                pl: 2,
                ml: 1,
                fontStyle: "italic",
                opacity: 0.8,
              }}
            >
              {children}
            </Box>
          ),
        }}
      >
        {escapedContent}
      </ReactMarkdown>
    </Box>
  );
}
